import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import PolicyRulesEditor from "@/components/PolicyRulesEditor";

// Counts, not collections. `members` is the list on this endpoint, which is
// why the count lives under its own key — see fleet.tenant_rollup.
const STAT = [
  { k: "member_count", label: "Members" },
  { k: "connector_count", label: "Connectors" },
  { k: "connector_failing_count", label: "Failing" },
  { k: "case_count", label: "Cases" },
];

export default function PlatformTenantDetail() {
  const { id } = useParams();
  const [tenant, setTenant] = useState(null);
  const [events, setEvents] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  // Bumped after a save, to re-read the ruleset without remounting the page.
  const [reloads, setReloads] = useState(0);
  const nav = useNavigate();

  // Both reads settle together and update once. Two independent fetches each
  // setting their own state renders the page twice and, more importantly,
  // leaves a second update in flight after the first has painted — which in
  // tests is an act() warning and in the browser is a visible reflow.
  // allSettled rather than all: a failing audit read should not blank the
  // workspace it annotates.
  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.get(`/platform/tenants/${id}`),
      api.get(`/platform/audit?tenant_id=${id}`),
      api.get(`/platform/tenants/${id}/policy`),
      api.get(`/platform/policy/fields`),
    ]).then(([detail, audit, pol, cat]) => {
      if (!alive) return;
      setTenant(detail.status === "fulfilled" ? detail.value.data : false);
      setEvents(audit.status === "fulfilled" ? audit.value.data.events || [] : []);
      setPolicy(pol.status === "fulfilled" ? pol.value.data : null);
      setCatalogue(cat.status === "fulfilled" ? cat.value.data : null);
    });
    return () => { alive = false; };
  }, [id, reloads]);

  if (tenant === null) {
    return <div className="p-8 text-[13px]" style={{ color: "var(--text-muted)" }}>Loading workspace…</div>;
  }
  if (tenant === false) {
    return <div className="p-8 text-[13px]" style={{ color: "var(--text-muted)" }}>No such workspace.</div>;
  }

  return (
    <div className="px-4 py-5 md:px-8 md:py-6" data-testid="platform-tenant-detail">
      <button className="btn btn-ghost mb-4" onClick={() => nav("/admin")} data-testid="back-to-tenants">
        <ArrowLeft size={12} /> All workspaces
      </button>

      <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--text-muted)" }}>
        {tenant.environment} workspace
      </div>
      <h1 className="font-display text-[26px]">{tenant.name}</h1>
      <p className="text-[13px] mt-1 font-mono" style={{ color: "var(--text-secondary)" }}>
        {tenant.slug} · {tenant.plan_code || "unprovisioned"}
        {tenant.billing_term && ` (${tenant.billing_term.replace(/_/g, " ")}`}
        {tenant.billing_term && tenant.period_end
          && `, until ${new Date(tenant.period_end).toLocaleDateString("en-IN")}`}
        {tenant.billing_term && ")"} · {tenant.status}
      </p>

      <div className="grid grid-cols-12 gap-4 mt-5">
        {STAT.map((s) => (
          <div key={s.k} className="card p-4 col-span-6 md:col-span-3" data-testid={`stat-${s.k}`}>
            <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{s.label}</div>
            <div className="font-mono text-[24px] mt-1">{tenant[s.k] ?? 0}</div>
          </div>
        ))}
      </div>

      <h2 className="font-display text-[18px] mt-8 mb-3">People</h2>
      <div className="card overflow-x-auto" data-testid="tenant-members">
        <table className="w-full min-w-[640px] text-[13px]">
          <thead>
            <tr className="text-left" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Email</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Name</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Role</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody>
            {(tenant.members || []).length === 0 && (
              <tr><td colSpan={4} className="py-8 text-center" style={{ color: "var(--text-muted)" }}>
                No members. Nobody can sign in to this workspace.
              </td></tr>
            )}
            {(tenant.members || []).map((m) => (
              <tr key={m.user_id} style={{ borderBottom: "1px solid var(--border)" }}
                  data-testid={`tenant-member-${m.email}`}>
                <td className="py-2.5 px-3">{m.email}</td>
                <td className="py-2.5 px-3">{m.name}</td>
                <td className="py-2.5 px-3"><span className="badge b-muted">{m.role}</span></td>
                <td className="py-2.5 px-3">
                  <span className={`badge ${m.status === "active" ? "b-healthy" : "b-muted"}`}>{m.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8">
        <PolicyRulesEditor
          /* Remounts after a save so the editor restarts from the stored
             ruleset rather than from whatever was half-typed. */
          key={`${id}-${policy?.revision ?? 0}`}
          tenantId={id}
          policy={policy}
          catalogue={catalogue}
          onSaved={() => setReloads((n) => n + 1)}
        />
      </div>

      <h2 className="font-display text-[18px] mt-8 mb-3">What staff did here</h2>
      <div className="card overflow-x-auto" data-testid="tenant-staff-audit">
        <table className="w-full min-w-[640px] text-[13px]">
          <thead>
            <tr className="text-left" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">When</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Who</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Action</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Reason</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr><td colSpan={4} className="py-8 text-center" style={{ color: "var(--text-muted)" }}>
                No staff actions recorded.
              </td></tr>
            )}
            {events.map((e) => (
              <tr key={e.id} style={{ borderBottom: "1px solid var(--border)" }}>
                <td className="py-2.5 px-3 font-mono text-[11.5px]">{e.created_at}</td>
                <td className="py-2.5 px-3">{e.actor_email}</td>
                <td className="py-2.5 px-3 font-mono text-[12px]">{e.action_type}</td>
                <td className="py-2.5 px-3" style={{ color: "var(--text-secondary)" }}>{e.reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
